import React, { useState, useEffect, useCallback } from 'react';
import { useDataProvider, useNotify, useRefresh, useGetIdentity } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Stack,
  Button,
  Typography,
  Divider,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';

interface Customer {
  id: string | number;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
}

interface Lead {
  id: string | number;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
}

interface Board {
  id: string | number;
  name: string;
  stages?: BoardStage[];
}

interface BoardStage {
  id: string;
  name: string;
  description?: string;
  color?: string;
  sortOrder: number;
  isDefault: boolean;
  isClosedWon: boolean;
  isClosedLost: boolean;
  boardId: string;
}

interface User {
  id: string | number;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
}

interface CreateOpportunityDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultCustomerId?: string | number | null;
  defaultBoardId?: string | number | null;
  defaultLeadId?: string | number | null;
  defaultCustomerName?: string;
  editMode?: boolean;
  opportunityId?: string;
  initialData?: {
    name?: string;
    customerId?: string | number;
    boardId?: string | number;
    value?: number;
    probability?: number;
    expectedCloseDate?: string;
    actualCloseDate?: string;
    source?: string;
    lostReason?: string;
    stageId?: string;
    description?: string;
    assignedUserId?: string | number;
  };
}

export const CreateOpportunityDialog: React.FC<CreateOpportunityDialogProps> = ({
  open,
  onClose,
  onSuccess,
  defaultCustomerId = null,
  defaultBoardId = null,
  defaultLeadId = null,
  defaultCustomerName = '',
  editMode = false,
  opportunityId,
  initialData,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { data: identity } = useGetIdentity();

  // Helper function to format date for HTML date input (YYYY-MM-DD)
  // Avoids timezone conversion issues by parsing the date string directly
  const formatDateForInput = (dateString: string | null | undefined): string | null => {
    if (!dateString) return null;
    try {
      // If the date string is already in YYYY-MM-DD format, return it as-is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      
      // For ISO strings, extract just the date part to avoid timezone conversion
      if (dateString.includes('T')) {
        return dateString.split('T')[0];
      }
      
      // Fallback: try to parse and format, but this might have timezone issues
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      return date.toISOString().split('T')[0];
    } catch {
      return null;
    }
  };

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [boards, setBoards] = useState<Board[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [step, setStep] = useState(1); // 1: Customer/Lead selection, 2: Opportunity details
  const [stages, setStages] = useState<BoardStage[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [loadingStages, setLoadingStages] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedEntityType, setSelectedEntityType] = useState<'customer' | 'lead' | null>(null);
  const [opportunityFormData, setOpportunityFormData] = useState({
    name: editMode ? initialData?.name || '' : defaultCustomerName || '',
    customerId: editMode ? initialData?.customerId || null : defaultCustomerId,
    leadId: editMode ? initialData?.leadId || null : defaultLeadId,
    boardId: editMode ? initialData?.boardId || null : defaultBoardId,
    value: editMode ? initialData?.value || 0 : 0,
    probability: editMode ? initialData?.probability || 0 : 0,
    expectedCloseDate: editMode ? formatDateForInput(initialData?.expectedCloseDate) : null,
    actualCloseDate: editMode ? formatDateForInput(initialData?.actualCloseDate) : null,
    source: editMode ? initialData?.source || '' : '',
    lostReason: editMode ? initialData?.lostReason || '' : '',
    stageId: editMode ? initialData?.stageId || '' : '',
    description: editMode ? initialData?.description || '' : '',
    assignedUserId: editMode ? initialData?.assignedUserId || null : identity?.id || null,
  });

  const getCustomerDisplayName = (customer: Customer) => {
    return (
      customer.displayName ||
      `${customer.firstName || ''} ${customer.lastName || ''}`.trim() ||
      customer.company ||
      `Customer ${customer.id}`
    );
  };

  const getLeadDisplayName = (lead: Lead) => {
    return (
      lead.displayName ||
      `${lead.firstName || ''} ${lead.lastName || ''}`.trim() ||
      lead.company ||
      `Lead ${lead.id}`
    );
  };

  const getUserDisplayName = (user: User) => {
    return (
      user.displayName ||
      `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
      user.name ||
      user.email ||
      `User ${user.id}`
    );
  };

  const getDefaultOpportunityName = useCallback(
    (customerId: string | number | null) => {
      if (!customerId || !customers.length) {
        return '';
      }
      const customer = customers.find(c => c.id === customerId);
      return customer ? getCustomerDisplayName(customer) : '';
    },
    [customers]
  );

  const loadCustomers = useCallback(async () => {
    // Only load customers if we're creating from opportunities screen (no default customer or lead)
    if ((defaultCustomerId || defaultLeadId) || customers.length > 0) {
      return;
    }
    setLoading(true);
    try {
      const result = await dataProvider.getList('customers', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'displayName', order: 'ASC' },
        filter: {},
      });
      // Sort customers alphabetically by display name
      const sortedCustomers = result.data.sort((a, b) => {
        const nameA = getCustomerDisplayName(a).toLowerCase();
        const nameB = getCustomerDisplayName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setCustomers(sortedCustomers);
    } catch (error) {
      notify('Failed to load customers', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [defaultCustomerId, defaultLeadId, customers.length, dataProvider, notify]);

  const loadLeads = useCallback(async () => {
    // Only load leads if we're creating from opportunities screen (no default customer or lead)
    if ((defaultCustomerId || defaultLeadId) || leads.length > 0) {
      return;
    }
    setLoadingLeads(true);
    try {
      const result = await dataProvider.getList('leads', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'displayName', order: 'ASC' },
        filter: {},
      });
      // Sort leads alphabetically by display name
      const sortedLeads = result.data.sort((a, b) => {
        const nameA = getLeadDisplayName(a).toLowerCase();
        const nameB = getLeadDisplayName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setLeads(sortedLeads);
    } catch (error) {
      notify('Failed to load leads', { type: 'error' });
    } finally {
      setLoadingLeads(false);
    }
  }, [defaultCustomerId, defaultLeadId, leads.length, dataProvider, notify]);

  const loadBoards = useCallback(async () => {
    if (boards.length > 0) {
      return;
    }
    try {
      const result = await dataProvider.getList('boards', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'name', order: 'ASC' },
        filter: { isActive: true },
      });
      setBoards(result.data);
      
      // Auto-select board based on context
      if (!editMode && result.data.length > 0 && !opportunityFormData.boardId) {
        let boardToSelect;
        if (defaultBoardId) {
          // Use the provided default board (e.g., when creating from board screen)
          boardToSelect = result.data.find(board => board.id === defaultBoardId) || result.data[0];
        } else {
          // Use first board when no default provided (e.g., when creating from opportunities screen)
          boardToSelect = result.data[0];
        }
        setOpportunityFormData(prev => ({ ...prev, boardId: boardToSelect.id }));
      }
    } catch (error) {
      notify('Failed to load boards', { type: 'error' });
    }
  }, [boards.length, dataProvider, notify, editMode, defaultBoardId, opportunityFormData.boardId]);

  const loadUsers = useCallback(async () => {
    if (users.length > 0) {
      return;
    }
    try {
      const result = await dataProvider.getList('users', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'name', order: 'ASC' },
        filter: {},
      });
      setUsers(result.data);
    } catch (error) {
      notify('Failed to load users', { type: 'error' });
    }
  }, [users.length, dataProvider, notify]);

  const loadStagesForBoard = useCallback(
    async (boardId: string | number) => {
      if (!boardId) {
        setStages([]);
        return;
      }

      setLoadingStages(true);
      try {
        const { data: board } = await dataProvider.getOne('boards', { id: boardId });
        const sortedStages = board.stages
          ? [...board.stages].sort((a, b) => a.sortOrder - b.sortOrder)
          : [];
        setStages(sortedStages);

        // Auto-select default stage if available, otherwise select first stage
        const defaultStage = sortedStages.find(stage => stage.isDefault);
        const stageToSelect = defaultStage || sortedStages[0];
        
        if (stageToSelect) {
          setOpportunityFormData(prev => {
            // Only set if stageId is empty
            if (!prev.stageId) {
              return { ...prev, stageId: stageToSelect.id };
            }
            return prev;
          });
        }
      } catch (error) {
        notify('Failed to load stages', { type: 'error' });
      } finally {
        setLoadingStages(false);
      }
    },
    [dataProvider, notify]
  );

  const resetForm = () => {
    if (editMode && initialData) {
      // Reset to initial data in edit mode
      setOpportunityFormData({
        name: initialData.name || '',
        customerId: initialData.customerId || null,
        leadId: initialData.leadId || null,
        boardId: initialData.boardId || null,
        value: initialData.value || 0,
        probability: initialData.probability || 0,
        expectedCloseDate: formatDateForInput(initialData.expectedCloseDate),
        stageId: initialData.stageId || '',
        description: initialData.description || '',
        assignedUserId: initialData.assignedUserId || null,
      });
      setStep(2); // Go directly to step 2 in edit mode
    } else {
      // Reset to defaults in create mode
      const defaultName =
        defaultCustomerName ||
        (defaultCustomerId ? getDefaultOpportunityName(defaultCustomerId) : '');
      setOpportunityFormData({
        name: defaultName,
        customerId: defaultCustomerId,
        leadId: defaultLeadId,
        boardId: defaultBoardId,
        value: 0,
        probability: 0,
        expectedCloseDate: null,
        stageId: '',
        description: '',
        assignedUserId: identity?.id || null,
      });
      
      // Load stages immediately if we have a default board
      if (defaultBoardId) {
        loadStagesForBoard(defaultBoardId);
      }
      
      // Set step based on context
      if (defaultCustomerId || defaultLeadId) {
        setStep(2); // Skip customer/lead selection if we have defaults
      } else {
        setStep(1); // Start with customer/lead selection
      }
    }
    setSelectedEntityType(null);
  };

  const handleCreateOpportunity = async () => {
    // Validation
    if (!opportunityFormData.name?.trim()) {
      notify('Opportunity name is required', { type: 'error' });
      return;
    }

    // Determine the actual customer ID and lead ID to use
    const actualCustomerId = opportunityFormData.customerId || defaultCustomerId;
    const actualLeadId = opportunityFormData.leadId || defaultLeadId;
    
    if (!actualCustomerId && !actualLeadId) {
      notify('Customer or lead is required', { type: 'error' });
      return;
    }

    if (opportunityFormData.probability < 0 || opportunityFormData.probability > 100) {
      notify('Probability must be between 0 and 100', { type: 'error' });
      return;
    }

    if (!opportunityFormData.boardId) {
      notify('Board is required', { type: 'error' });
      return;
    }

    if (!opportunityFormData.stageId) {
      notify('Stage is required', { type: 'error' });
      return;
    }

    const formData = {
      ...opportunityFormData,
      customerId: actualCustomerId,
      leadId: actualLeadId,
      boardId: opportunityFormData.boardId || defaultBoardId,
    };

    try {
      setSaving(true);
      if (editMode && opportunityId) {
        // Update existing opportunity
        await dataProvider.update('opportunities', {
          id: opportunityId,
          data: formData,
          previousData: initialData,
        });
        notify('Opportunity updated successfully!', { type: 'success' });
      } else {
        // Create new opportunity
        await dataProvider.create('opportunities', {
          data: formData,
        });
        notify('Opportunity created successfully!', { type: 'success' });
      }
      onSuccess?.();
      onClose();
      resetForm();
      refresh(); // Refresh the current page
    } catch (error) {
      notify(`Failed to ${editMode ? 'update' : 'create'} opportunity. Please try again.`, {
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDialogClose = () => {
    onClose();
    resetForm();
  };

  const handleNextStep = () => {
    if (step === 1) {
      // Validate that either customer or lead is selected
      if (!opportunityFormData.customerId && !opportunityFormData.leadId) {
        notify('Please select a customer or lead', { type: 'error' });
        return;
      }
      
      // Set opportunity name based on selected customer/lead
      let selectedName = '';
      if (opportunityFormData.customerId) {
        const customer = customers.find(c => c.id === opportunityFormData.customerId);
        selectedName = customer ? getCustomerDisplayName(customer) : '';
      } else if (opportunityFormData.leadId) {
        const lead = leads.find(l => l.id === opportunityFormData.leadId);
        selectedName = lead ? getLeadDisplayName(lead) : '';
      }
      
      if (selectedName && !opportunityFormData.name) {
        setOpportunityFormData(prev => ({ ...prev, name: selectedName }));
      }
      
      setStep(2);
    }
  };

  const handleBackStep = () => {
    if (step === 2) {
      setStep(1);
    }
  };

  const handleDeleteOpportunity = async () => {
    if (!opportunityId || !editMode) return;
    
    if (!confirm('Are you sure you want to delete this opportunity? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      await dataProvider.delete('opportunities', {
        id: opportunityId,
        previousData: initialData,
      });
      notify('Opportunity deleted successfully!', { type: 'success' });
      onClose();
      refresh();
      // Navigate back to opportunities list or board
      window.history.back();
    } catch (error) {
      notify('Failed to delete opportunity. Please try again.', {
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Load data when dialog opens
  useEffect(() => {
    if (open) {
      loadCustomers();
      loadLeads();
      loadBoards();
      loadUsers();
      
      // Set proper step when dialog opens
      if (editMode) {
        setStep(2); // Always go to step 2 in edit mode
      } else if (defaultCustomerId || defaultLeadId) {
        setStep(2); // Skip step 1 if we have default customer/lead
      } else {
        setStep(1); // Start with step 1 for new opportunities from opportunities screen
      }
    }
  }, [open, editMode, defaultCustomerId, defaultLeadId, loadCustomers, loadLeads, loadBoards, loadUsers]);

  // Set default opportunity name when we have customer data and default customer ID
  useEffect(() => {
    if (
      defaultCustomerId &&
      !defaultCustomerName &&
      customers.length > 0
    ) {
      const defaultName = getDefaultOpportunityName(defaultCustomerId);
      if (defaultName && opportunityFormData.name === '') {
        setOpportunityFormData(prev => ({ ...prev, name: defaultName }));
      }
    }
  }, [
    defaultCustomerId,
    customers.length,
    opportunityFormData.name,
    defaultCustomerName,
    getDefaultOpportunityName,
  ]);

  // Set default opportunity name from prop when provided
  useEffect(() => {
    if (defaultCustomerName && opportunityFormData.name === '') {
      setOpportunityFormData(prev => ({ ...prev, name: defaultCustomerName }));
    }
  }, [defaultCustomerName, opportunityFormData.name]);

  // Set default assigned user when identity is loaded
  useEffect(() => {
    if (identity?.id && opportunityFormData.assignedUserId === null) {
      setOpportunityFormData(prev => ({ ...prev, assignedUserId: identity.id }));
    }
  }, [identity, opportunityFormData.assignedUserId]);

  // Update opportunity name when customer changes (only when creating from opportunities screen)
  useEffect(() => {
    if (!defaultCustomerId && !defaultLeadId && opportunityFormData.customerId && customers.length > 0 && step === 2) {
      const customerName = getDefaultOpportunityName(opportunityFormData.customerId);
      setOpportunityFormData(prev => ({ ...prev, name: customerName }));
    }
  }, [opportunityFormData.customerId, customers, defaultCustomerId, defaultLeadId, getDefaultOpportunityName, step]);

  // Load stages when board changes
  useEffect(() => {
    if (opportunityFormData.boardId) {
      loadStagesForBoard(opportunityFormData.boardId);
    } else {
      setStages([]);
      setOpportunityFormData(prev => ({ ...prev, stageId: '' }));
    }
  }, [opportunityFormData.boardId, loadStagesForBoard]);

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editMode ? 'Edit Opportunity' : 'Create New Opportunity'}
        {!editMode && !defaultCustomerId && !defaultLeadId && (
          <Typography variant="caption" display="block" sx={{ fontWeight: 'normal', mt: 0.5 }}>
            Step {step} of 2
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Step 1: Customer/Lead Selection (only when creating from opportunities screen) */}
          {step === 1 && !defaultCustomerId && !defaultLeadId && (
            <Stack spacing={3}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Opportunity Type
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                Is this opportunity for an existing customer or a new lead?
              </Typography>
              
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Opportunity Type</InputLabel>
                  <Select
                    value={selectedEntityType || ''}
                    label="Opportunity Type"
                    onChange={(e) => {
                      const entityType = e.target.value as 'customer' | 'lead';
                      setSelectedEntityType(entityType);
                      // Reset the other field when switching types
                      setOpportunityFormData(prev => ({
                        ...prev,
                        customerId: entityType === 'customer' ? prev.customerId : null,
                        leadId: entityType === 'lead' ? prev.leadId : undefined
                      }));
                    }}
                  >
                    <MenuItem value="customer">Customer</MenuItem>
                    <MenuItem value="lead">Lead</MenuItem>
                  </Select>
                </FormControl>
                
                {selectedEntityType === 'customer' && (
                  <Autocomplete
                    options={customers}
                    getOptionLabel={getCustomerDisplayName}
                    value={customers.find(c => c.id === opportunityFormData.customerId) || null}
                    onChange={(event, value) => {
                      const customerId = value ? value.id : null;
                      setOpportunityFormData({
                        ...opportunityFormData,
                        customerId,
                      });
                    }}
                    loading={loading}
                    renderInput={params => (
                      <MuiTextField
                        {...params}
                        label="Customer"
                        required
                        helperText="Search and select a customer by name or company"
                      />
                    )}
                    filterOptions={(options, { inputValue }) =>
                      options.filter(option =>
                        getCustomerDisplayName(option).toLowerCase().includes(inputValue.toLowerCase())
                      )
                    }
                  />
                )}
                
                {selectedEntityType === 'lead' && (
                  <Autocomplete
                    options={leads}
                    getOptionLabel={getLeadDisplayName}
                    value={leads.find(l => l.id === opportunityFormData.leadId) || null}
                    onChange={(event, value) => {
                      const leadId = value ? value.id : null;
                      setOpportunityFormData({
                        ...opportunityFormData,
                        leadId: leadId,
                        customerId: null // Clear customer when selecting lead
                      });
                    }}
                    loading={loadingLeads}
                    renderInput={params => (
                      <MuiTextField
                        {...params}
                        label="Lead"
                        required
                        helperText="Search and select a lead by name or company"
                      />
                    )}
                    filterOptions={(options, { inputValue }) =>
                      options.filter(option =>
                        getLeadDisplayName(option).toLowerCase().includes(inputValue.toLowerCase())
                      )
                    }
                  />
                )}
              </Stack>
            </Stack>
          )}
          
          {/* Step 2: Opportunity Details */}
          {step === 2 && (
            <>
              {/* Basic Information Section */}
              <Stack spacing={2}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Basic Information
                </Typography>

                <MuiTextField
                  label="Opportunity Name"
                  value={opportunityFormData.name}
                  onChange={e => setOpportunityFormData({ ...opportunityFormData, name: e.target.value })}
                  fullWidth
                  required
                />

                <MuiTextField
                  label="Lead Source"
                  value={opportunityFormData.source}
                  onChange={e => setOpportunityFormData({ ...opportunityFormData, source: e.target.value })}
                  fullWidth
                  placeholder="e.g., Website, Referral, Cold Call, Trade Show"
                />
              </Stack>

              <Divider />

              {/* Pipeline & Stages Section */}
              <Stack spacing={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Pipeline & Stage
            </Typography>

            <Autocomplete
              options={boards}
              getOptionLabel={board => board.name || `Board ${board.id}`}
              value={boards.find(b => b.id === opportunityFormData.boardId) || null}
              onChange={(event, value) => {
                const boardId = value ? value.id : null;
                setOpportunityFormData({
                  ...opportunityFormData,
                  boardId: boardId,
                  stageId: '', // Reset stage when board changes
                });
              }}
              renderInput={params => (
                <MuiTextField
                  {...params}
                  label="Board"
                  required
                  helperText="Select a board for this opportunity"
                />
              )}
            />

            <FormControl fullWidth required>
              <InputLabel>Stage</InputLabel>
              <Select
                value={opportunityFormData.stageId || ''}
                label="Stage"
                onChange={e =>
                  setOpportunityFormData({ ...opportunityFormData, stageId: e.target.value })
                }
                disabled={!opportunityFormData.boardId || loadingStages}
              >
                {stages.length === 0 && !loadingStages && (
                  <MenuItem value="" disabled>
                    {opportunityFormData.boardId ? 'No stages available' : 'Select a board first'}
                  </MenuItem>
                )}
                {stages.map(stage => (
                  <MenuItem key={stage.id} value={stage.id}>
                    {stage.name}
                  </MenuItem>
                ))}
              </Select>
                </FormControl>
              </Stack>

              <Divider />

              {/* Value & Timeline Section */}
              <Stack spacing={2}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Value & Timeline
                </Typography>

                <MuiTextField
                  label="Estimated Value"
                  type="number"
                  value={opportunityFormData.value === 0 ? '' : opportunityFormData.value}
                  onChange={e =>
                    setOpportunityFormData({
                      ...opportunityFormData,
                      value: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0,
                    })
                  }
                  fullWidth
                  placeholder="0"
                />

                <MuiTextField
                  label="Probability (%)"
                  type="number"
                  value={opportunityFormData.probability === 0 ? '' : opportunityFormData.probability}
                  onChange={e =>
                    setOpportunityFormData({
                      ...opportunityFormData,
                      probability: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0,
                    })
                  }
                  fullWidth
                  inputProps={{ min: 0, max: 100 }}
                  placeholder="0"
                />

                <MuiTextField
                  label="Expected Close Date"
                  type="date"
                  value={opportunityFormData.expectedCloseDate || ''}
                  onChange={e =>
                    setOpportunityFormData({ ...opportunityFormData, expectedCloseDate: e.target.value })
                  }
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />

                {editMode && (
                  <MuiTextField
                    label="Actual Close Date"
                    type="date"
                    value={opportunityFormData.actualCloseDate || ''}
                    onChange={e =>
                      setOpportunityFormData({ ...opportunityFormData, actualCloseDate: e.target.value })
                    }
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText="Leave empty if opportunity is not yet closed"
                  />
                )}


                {editMode && stages.find(stage => stage.id === opportunityFormData.stageId)?.isClosedLost && (
                  <MuiTextField
                    label="Lost Reason"
                    value={opportunityFormData.lostReason}
                    onChange={e => setOpportunityFormData({ ...opportunityFormData, lostReason: e.target.value })}
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Why was this opportunity lost?"
                  />
                )}
              </Stack>

              <Divider />

              {/* Assignment & Details Section */}
              <Stack spacing={2}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Assignment & Details
                </Typography>

                <Autocomplete
                  options={users}
                  getOptionLabel={getUserDisplayName}
                  value={users.find(u => u.id === opportunityFormData.assignedUserId) || null}
                  onChange={(event, value) => {
                    setOpportunityFormData({
                      ...opportunityFormData,
                      assignedUserId: value ? value.id : null,
                    });
                  }}
                  renderInput={params => (
                    <MuiTextField
                      {...params}
                      label="Assigned User"
                      helperText="Select a user to assign this opportunity to"
                    />
                  )}
                  filterOptions={(options, { inputValue }) =>
                    options.filter(option =>
                      getUserDisplayName(option).toLowerCase().includes(inputValue.toLowerCase())
                    )
                  }
                />

                <MuiTextField
                  label="Description"
                  value={opportunityFormData.description}
                  onChange={e =>
                    setOpportunityFormData({ ...opportunityFormData, description: e.target.value })
                  }
                  multiline
                  rows={3}
                  fullWidth
                />
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {step === 2 && !editMode && !defaultCustomerId && !defaultLeadId && (
          <Button onClick={handleBackStep}>Back</Button>
        )}
        {editMode && (
          <Button 
            onClick={handleDeleteOpportunity}
            startIcon={<DeleteIcon />}
            color="error"
            disabled={saving}
            sx={{ mr: 'auto' }}
          >
            Delete
          </Button>
        )}
        <Button onClick={handleDialogClose}>Cancel</Button>
        {step === 1 && !defaultCustomerId && !defaultLeadId ? (
          <Button
            onClick={handleNextStep}
            variant="contained"
            disabled={loading || loadingLeads || (!opportunityFormData.customerId && !opportunityFormData.leadId)}
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleCreateOpportunity}
            variant="contained"
            disabled={
              saving ||
              !opportunityFormData.name ||
              (!defaultCustomerId && !defaultLeadId && !opportunityFormData.customerId && !opportunityFormData.leadId) ||
              !opportunityFormData.boardId ||
              !opportunityFormData.stageId
            }
          >
            {saving
              ? editMode
                ? 'Updating...'
                : 'Creating...'
              : editMode
              ? 'Update Opportunity'
              : 'Create Opportunity'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
